import { useState, useEffect, useRef, useCallback } from "react";
import { Bell, X, Check, Info, Box, Truck, MessageSquare, AlertTriangle, Megaphone } from "lucide-react";
import { collection, query, orderBy, limit, getDocs, doc, updateDoc, writeBatch, where } from "firebase/firestore";
import { db } from "../firebase";
import useCurrentUser from "../hooks/useCurrentUser";
import { useNavigate } from "react-router-dom";
import { formatDistanceToNow } from "date-fns";
import { isHasbroRole } from "./hasbro/hasbroTheme";

// A listener the security rules refuse ends quietly (empty list) instead of an uncaught error
const quiet = (what, setEmpty) => err => { console.warn(`Notifications: ${what} unavailable (${err.code || err.message})`); setEmpty([]); };

/** @param workspace  the layout's role ("hasbro", "client", "super-admin"…) — Hasbro pages have no client notifications */
export default function NotificationDropdown({ workspace }) {
    const { clientId, role } = useCurrentUser();
    const [notifications, setNotifications] = useState([]);
    const [isOpen, setIsOpen] = useState(false);
    const [unreadCount, setUnreadCount] = useState(0);
    const dropdownRef = useRef(null);
    const navigate = useNavigate();

    // Close dropdown when clicking outside
    useEffect(() => {
        function handleClickOutside(event) {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        }
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const [regularNotes, setRegularNotes] = useState([]);
    const [announcements, setAnnouncements] = useState([]);

    // Loaded when the page opens and again each time the bell is opened — no live listeners,
    // so nothing reads the database while the app just sits open
    const loadAnnouncements = useCallback(() => {
        getDocs(query(collection(db, "announcements"), where("status", "==", "active")))
            .then(snap => setAnnouncements(snap.docs.map(d => ({ id: d.id, ...d.data(), isAnnouncement: true }))))
            .catch(quiet("announcements", setAnnouncements));
    }, []);

    const loadNotifications = useCallback(() => {
        if (!clientId && role !== "super-admin" && role !== "admin") return;
        if (isHasbroRole(workspace)) return;      // Hasbro workspace: no client notification feed

        const q = role === "super-admin" || role === "admin"
            ? query(collection(db, "notifications"), orderBy("createdAt", "desc"), limit(20))
            : query(collection(db, "Clients", clientId, "notifications"), orderBy("timestamp", "desc"), limit(20));
        getDocs(q)
            .then(snap => setRegularNotes(snap.docs.map(d => ({ id: d.id, ...d.data() }))))
            .catch(quiet("notifications", setRegularNotes));
    }, [clientId, role, workspace]);

    useEffect(() => { loadAnnouncements(); }, [loadAnnouncements]);
    useEffect(() => { loadNotifications(); }, [loadNotifications]);
    useEffect(() => {
        if (isOpen) { loadAnnouncements(); loadNotifications(); }
    }, [isOpen, loadAnnouncements, loadNotifications]);

    useEffect(() => {
        const validAnnouncements = announcements.filter(a => {
            if (a.expiresAt?.toDate && a.expiresAt.toDate() < new Date()) return false;
            if (a.targetType === 'allUsers') return true;
            if (a.targetType === 'allTenants' && ['client', 'agent', '3pl'].includes(role)) return true;
            if (a.targetType === 'tenant' && a.targetId === clientId) return true;
            return false;
        }).map(a => ({
            id: a.id,
            title: a.title,
            message: a.message,
            type: 'megaphone',
            read: true,
            timestamp: a.createdAt,
            isAnnouncement: true
        }));

        const combined = [...regularNotes, ...validAnnouncements].sort((a, b) => {
            // Unify timestamps
            const getTimestamp = (obj) => {
                if (obj.timestamp?.seconds) return obj.timestamp.seconds;
                if (obj.timestamp?.toDate) return Math.floor(obj.timestamp.toDate().getTime() / 1000);
                if (obj.createdAt?.seconds) return obj.createdAt.seconds;
                if (obj.createdAt?.toDate) return Math.floor(obj.createdAt.toDate().getTime() / 1000);
                return 0;
            };
            return getTimestamp(b) - getTimestamp(a);
        }).slice(0, 20); // Keep top 20

        setNotifications(combined);
        setUnreadCount(regularNotes.filter(n => !n.read).length);
    }, [regularNotes, announcements, role, clientId]);

    const handleMarkAsRead = async (id, isAnnouncement = false) => {
        if (isAnnouncement) return; // Announcements are global and treated as read in the bell
        try {
            let noteRef;
            if (role === "super-admin") {
                noteRef = doc(db, "notifications", id);
            } else {
                if (!clientId) return;
                noteRef = doc(db, "Clients", clientId, "notifications", id);
            }
            await updateDoc(noteRef, { read: true });
            setRegularNotes(prev => prev.map(n => (n.id === id ? { ...n, read: true } : n)));
        } catch (err) {
            console.error("Error marking notification as read:", err);
        }
    };

    const handleMarkAllRead = async () => {
        if (unreadCount === 0) return;
        try {
            const batch = writeBatch(db);
            const unreadNotes = notifications.filter(n => !n.read);

            unreadNotes.forEach(note => {
                let noteRef;
                if (role === "super-admin") {
                    noteRef = doc(db, "notifications", note.id);
                } else {
                    if (!clientId) return;
                    noteRef = doc(db, "Clients", clientId, "notifications", note.id);
                }
                batch.update(noteRef, { read: true });
            });

            await batch.commit();
            setRegularNotes(prev => prev.map(n => ({ ...n, read: true })));
        } catch (err) {
            console.error("Error marking all as read:", err);
        }
    };

    const handleNotificationClick = async (notification) => {
        if (!notification.read && !notification.isAnnouncement) {
            handleMarkAsRead(notification.id);
        }
        if (notification.link) {
            navigate(notification.link);
            setIsOpen(false);
        }
    };

    const getIcon = (type) => {
        switch (type) {
            case 'shipment': return <Truck size={16} className="text-blue-500" />;
            case 'inventory': return <Box size={16} className="text-gold-500" />;
            case 'message': return <MessageSquare size={16} className="text-emerald-500" />;
            case 'alert': return <AlertTriangle size={16} className="text-red-500" />;
            case 'megaphone': return <Megaphone size={16} className="text-purple-500" />;
            default: return <Info size={16} className="text-navy-500" />;
        }
    };

    return (
        <div className="relative" ref={dropdownRef}>
            <button
                onClick={() => setIsOpen(!isOpen)}
                className="relative p-2 text-slate-400 hover:text-navy-900 transition-colors rounded-full hover:bg-slate-100"
            >
                <Bell size={20} />
                {unreadCount > 0 && (
                    <span className="absolute top-2 right-2 w-2 h-2 bg-red-500 rounded-full border border-white ring-1 ring-white"></span>
                )}
            </button>

            {isOpen && (
                <div className="absolute right-0 mt-2 w-80 bg-white rounded-xl shadow-2xl border border-slate-100 z-50 animate-in fade-in slide-in-from-top-2 duration-200">
                    {/* Header */}
                    <div className="flex items-center justify-between p-4 border-b border-slate-100 bg-slate-50/50 rounded-t-xl">
                        <h3 className="font-bold text-navy-900 text-sm">Notifications</h3>
                        {unreadCount > 0 && (
                            <button
                                onClick={handleMarkAllRead}
                                className="text-xs font-medium text-blue-600 hover:text-blue-700 flex items-center gap-1"
                            >
                                <Check size={12} /> Mark all read
                            </button>
                        )}
                    </div>

                    {/* List */}
                    <div className="max-h-96 overflow-y-auto scrollbar-thin scrollbar-thumb-slate-200">
                        {notifications.length === 0 ? (
                            <div className="p-8 text-center text-slate-400 text-sm flex flex-col items-center gap-2">
                                <div className="w-10 h-10 bg-slate-50 rounded-full flex items-center justify-center">
                                    <Bell size={18} className="text-slate-300" />
                                </div>
                                No notifications yet.
                            </div>
                        ) : (
                            <div className="divide-y divide-slate-50">
                                {notifications.map((note) => (
                                    <div
                                        key={note.id}
                                        onClick={() => handleNotificationClick(note)}
                                        className={`p-4 hover:bg-slate-50 transition-colors cursor-pointer group relative ${!note.read && !note.isAnnouncement ? "bg-blue-50/30" : ""}`}
                                    >
                                        <div className="flex gap-3">
                                            <div className={`mt-0.5 w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${!note.read && !note.isAnnouncement ? "bg-white shadow-sm ring-1 ring-slate-100" : "bg-slate-100"}`}>
                                                {getIcon(note.type)}
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <div className="flex justify-between items-start mb-0.5">
                                                    <p className={`text-sm font-medium truncate ${!note.read && !note.isAnnouncement ? "text-navy-900" : "text-slate-600"} ${note.isAnnouncement ? "text-purple-800" : ""}`}>
                                                        {note.title}
                                                    </p>
                                                    <span className="text-[10px] text-slate-400 whitespace-nowrap ml-2">
                                                        {note.timestamp?.seconds
                                                            ? formatDistanceToNow(new Date(note.timestamp.seconds * 1000), { addSuffix: true })
                                                            : note.createdAt?.seconds
                                                                ? formatDistanceToNow(new Date(note.createdAt.seconds * 1000), { addSuffix: true })
                                                                : "Just now"
                                                        }
                                                    </span>
                                                </div>
                                                <p className="text-xs text-slate-500 line-clamp-2 leading-relaxed">
                                                    {note.message}
                                                </p>
                                            </div>
                                            {!note.read && !note.isAnnouncement && (
                                                <div className="self-center">
                                                    <div className="w-1.5 h-1.5 bg-blue-500 rounded-full"></div>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* Footer */}
                    <div className="p-2 border-t border-slate-100 bg-slate-50/50 rounded-b-xl text-center">
                        <button className="text-[10px] font-medium text-slate-500 hover:text-navy-900 uppercase tracking-wide">
                            View All History
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}
